/**
 * Zengin metin (rich text) yardımcıları.
 *
 * Açıklama alanları artık HTML olarak saklanabiliyor (paragraf, madde listesi,
 * sütun düzeni vb.). Eski kayıtlar düz metin olduğu için, düz metni de güvenli
 * şekilde HTML'e çeviren ve her durumda temizlenmiş (sanitize edilmiş) HTML
 * döndüren yardımcılar burada toplanır.
 */

// İçeriğin HTML mi yoksa düz metin mi olduğunu kabaca tespit eder.
export function isHtmlContent(value: string | null | undefined): boolean {
  if (!value) return false;
  return /<([a-z][a-z0-9]*)\b[^>]*>/i.test(value);
}

function isEscapedHtmlContent(value: string): boolean {
  return /&lt;([a-z][a-z0-9]*)\b[\s\S]*?&gt;/i.test(value);
}

function decodeHtmlEntities(value: string): string {
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const ta = document.createElement('textarea');
    ta.innerHTML = value;
    return ta.value;
  }
  return value
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/gi, '&');
}

/** contentEditable'ın ürettiği geçersiz <p><ul>..</ul></p> sarmalayıcılarını sadeleştir */
export function normalizeListWrappers(html: string): string {
  return html
    .replace(/<p>\s*(<(?:ul|ol)\b[\s\S]*?<\/(?:ul|ol)>)\s*<\/p>/gi, '$1')
    .replace(/<p>\s*(<(?:ul|ol)\b)/gi, '$1')
    .replace(/(<\/(?:ul|ol)>)\s*<\/p>/gi, '$1');
}

// Düz metni paragraf/br yapısına çevirir (çift satır = yeni paragraf).
export function plainTextToHtml(text: string): string {
  const normalized = (text || '').replace(/\r\n/g, '\n');
  const blocks = normalized.split(/\n{2,}/);
  return blocks
    .map((block) => {
      const escaped = block
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
      return `<p>${escaped}</p>`;
    })
    .join('');
}

const ALLOWED_TAGS = new Set([
  'P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE',
  'UL', 'OL', 'LI', 'DIV', 'SPAN', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'A', 'BLOCKQUOTE',
  'IMG',
]);

// Yalnızca güvenli inline style özelliklerine izin ver (sütun düzeni + hizalama).
const ALLOWED_STYLE_PROPS = new Set([
  'column-count', 'columns', 'column-gap', 'text-align', 'font-weight', 'font-style', 'text-decoration', 'font-size',
  'max-width', 'width', 'height', 'display', 'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
  'border-radius', 'object-fit',
]);

function sanitizeStyle(style: string): string {
  return style
    .split(';')
    .map((decl) => decl.trim())
    .filter(Boolean)
    .filter((decl) => {
      const prop = decl.split(':')[0]?.trim().toLowerCase();
      return prop && ALLOWED_STYLE_PROPS.has(prop);
    })
    .join('; ');
}

function sanitizeNode(node: Node, doc: Document): void {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as HTMLElement;
      const tag = el.tagName.toUpperCase();

      if (!ALLOWED_TAGS.has(tag)) {
        // İzin verilmeyen etiketi (script, style, img, iframe vb.) kaldır,
        // fakat metin içeriğini koru.
        while (el.firstChild) {
          node.insertBefore(el.firstChild, el);
        }
        node.removeChild(el);
        continue;
      }

      // Tüm nitelikleri temizle; sadece güvenli olanları geri ekle.
      const href = tag === 'A' ? el.getAttribute('href') : null;
      const src = tag === 'IMG' ? el.getAttribute('src') : null;
      const alt = tag === 'IMG' ? el.getAttribute('alt') : null;
      const style = el.getAttribute('style');
      Array.from(el.attributes).forEach((attr) => el.removeAttribute(attr.name));

      if (style) {
        const cleaned = sanitizeStyle(style);
        if (cleaned) el.setAttribute('style', cleaned);
      }
      if (tag === 'A' && href && /^(https?:|mailto:|tel:|\/)/i.test(href.trim())) {
        el.setAttribute('href', href.trim());
        el.setAttribute('target', '_blank');
        el.setAttribute('rel', 'noopener noreferrer');
      }
      if (tag === 'IMG') {
        if (src && /^(https?:|data:image\/|\/)/i.test(src.trim())) {
          el.setAttribute('src', src.trim());
          if (alt) el.setAttribute('alt', alt);
        } else {
          node.removeChild(el);
          continue;
        }
      }

      sanitizeNode(el, doc);
    } else if (child.nodeType === Node.COMMENT_NODE) {
      node.removeChild(child);
    }
  }
}

// Güvenli HTML döndürür. Düz metin ise otomatik HTML'e çevirir.
export function sanitizeRichText(value: string | null | undefined): string {
  if (!value) return '';

  let raw = String(value);
  if (!isHtmlContent(raw) && isEscapedHtmlContent(raw)) {
    raw = decodeHtmlEntities(raw);
  }

  const html = isHtmlContent(raw) ? normalizeListWrappers(raw) : plainTextToHtml(raw);

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html;
  }

  const doc = new DOMParser().parseFromString(`<div id="__rt_root">${html}</div>`, 'text/html');
  const root = doc.getElementById('__rt_root');
  if (!root) return '';
  sanitizeNode(root, doc);
  return normalizeListWrappers(root.innerHTML);
}

// İçeriğin görsel olarak boş olup olmadığını kontrol eder (boş etiketler dahil).
export function isRichTextEmpty(value: string | null | undefined): boolean {
  if (!value) return true;
  const stripped = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return stripped.length === 0;
}
